import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import HandlerIcon from '@/assets/icons/HandlerIcon';
import { Flex, VFlex } from '@/components/base';
import IconButton from '@/components/Button/IconButton';
import Transition from '@/components/Common/Transition';
import Typo, { Paragraph } from '@/components/Typography/Typo';
import { usePublic } from '@/contexts/PublicContext';
import type { CreateChartPayload } from '@/entities/chart/api/types';
import chartUtils from '@/features/(charts)/chart-renderer/lib/chart-utils';
import { DashboardPreprocess } from '@/features/(charts)/chart-renderer/lib/ChartPreprocessFactory/ChartPreprocessFactory';
import { DASHBOARD_CARD_CHART_ALIAS_DATASET_KEY } from '@/features/Dashboard/types/constants';
import useColor from '@/hooks/domain/useColor';
import service from '@/services/index';
import { usePopup } from '@/stores/pure/PopupStore';

import Input from '../../../../components/Inputs/Input';
import DashboardCardStyles from '../../../Dashboard/components/styles/DashboardCardStyles';
import useReportCardDrag from '../../hooks/useReportCardDrag';
import useReportStore from '../../store/ReportStore';
import type { ReportColumnProps } from '../ReportEditor/ReportColumn';
import ReportChartProvider from './ReportChartProvider';

const {
  ChartWrapper,
  DashboardCardHeader,
  StyledDragArea,
  StyledDashboardCard,
  StyledDashboardCardWrapper,
} = DashboardCardStyles;

const ReportChartItem = (props: ReportColumnProps) => {
  const isPublic = usePublic();
  const report = props.report;
  const color = useColor();
  const setDragging = useReportStore((state) => state.setDragging);
  const removeColumn = useReportStore((state) => state.removeColumn);
  const checkHasAnalysis = useReportStore((state) => state.checkHasAnalysis);
  const setPopup = usePopup();
  const updateItem = useReportStore((state) => state.updateItem);
  const mode = useReportStore((state) => state.mode);
  const isReportEditMode = mode === 'EDIT';
  const [titleEditMode, setTitleEditMode] = useState(false);
  const { index, column, width } = props;

  const { dragRef, previewRef, isDragging } = useReportCardDrag({
    index,
    setSomeDragging: setDragging,
    column,
  });
  const chart = column.item.chart;
  if (!chart) throw new Error('chart is not exist');

  const [historical, setHistorical] = useState(
    chart.historical?.value ?? false,
  );
  const chartParams:
    | (ChartBase & { name: string })
    | (CreateChartPayload & { name: string }) = useMemo(() => {
    const params = {
      ...chart,
      historical: {
        value: historical,
        frequency: historical
          ? chartUtils.getFrequencyByTermUnit(chart.target.term.unit)
          : null,
      },
    };
    const preprocessFactory = new DashboardPreprocess({
      chart: params,
      historical,
      globalFilter: null,
      globalDateFilter: null,
    });
    const result = preprocessFactory.runPreprocess();
    return {
      ...result,
      name: chart.name,
    };
  }, [chart, historical]);

  const tableQuery = useQuery({
    queryKey: ['chart-table', report.alias, chart.alias],
    queryFn: () =>
      service.report.fetchChartTable(report.alias, chart.alias, isPublic),
    enabled: true,
  });

  const updateHistorical = async (historical: boolean) => {
    setHistorical(historical);
    const preprocessFactory = new DashboardPreprocess({
      chart,
      historical,
      globalFilter: null,
      globalDateFilter: null,
    });
    const params = preprocessFactory.runPreprocess();
    await service.chart.updateChart(chart.alias, params);
  };

  const onRemoveClick = async () => {
    const hasAnalysis = await checkHasAnalysis(chart.alias);
    if (hasAnalysis && hasAnalysis.length > 0) {
      setPopup({
        popupTheme: 'negative',
        type: 'confirm',
        title: 'Are you sure to remove this chart?',
        message: (
          <Paragraph type="body2">
            This chart is connected to one or more Smart Briefs. Removing the
            chart will also{' '}
            <span style={{ color: color.red600 }}>
              remove all linked Smart Briefs.
            </span>
          </Paragraph>
        ),
        confirmText: 'Remove',
        cancelText: 'Cancel',
        onConfirm: async () => {
          removeColumn(column.row_alias, column);
          hasAnalysis.forEach((alias) => {
            const getAnalysisColumn = () => {
              for (const row of report.mapped_rows) {
                const column = row.mapped_columns.find(
                  (col) => col.item_alias === alias,
                );
                if (column) {
                  return column;
                }
              }
              return null;
            };
            const analysisColumn = getAnalysisColumn();
            if (analysisColumn) {
              removeColumn(analysisColumn.row_alias, {
                alias: analysisColumn.alias,
                item_alias: analysisColumn.item_alias,
              });
            }
          });
        },
      });
      return;
    }

    removeColumn(column.row_alias, column);
  };

  const onEditClick = () => {
    setTitleEditMode(true);
  };

  useEffect(() => {
    if (!isReportEditMode) {
      setTitleEditMode(false);
    }
  }, [isReportEditMode]);

  return (
    <StyledDashboardCardWrapper
      ref={previewRef}
      isDragging={isDragging}
      width={width}
      style={{ height: '100%' }}
    >
      <StyledDashboardCard
        {...{
          [DASHBOARD_CARD_CHART_ALIAS_DATASET_KEY]: chart.alias,
        }}
      >
        {isReportEditMode && (
          <StyledDragArea ref={dragRef} className="drag-area-handler">
            <HandlerIcon />
          </StyledDragArea>
        )}

        <Transition
          status={true}
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Style>
            <DashboardCardHeader j="space-between" a="center">
              <Flex g={8} f={1}>
                {titleEditMode ? (
                  <Input
                    style={{ maxWidth: 400 }}
                    sizeType="l"
                    defaultValue={chart.name}
                    onBlur={(e) => {
                      const value = e.currentTarget.value;
                      updateItem(column.item.alias, {
                        chart: {
                          ...chart,
                          name: value,
                        },
                      });
                      setTitleEditMode(false);
                    }}
                  />
                ) : (
                  <Flex
                    g={4}
                    a="center"
                    onClick={() => isReportEditMode && onEditClick()}
                  >
                    <Typo type="body0" weight="m" multilineEllipsis={2}>
                      {chart.name}
                    </Typo>
                    {isReportEditMode && (
                      <IconButton
                        outlined={false}
                        icon={<EditOutlined />}
                        sizeType="m"
                        onClick={onEditClick}
                      />
                    )}
                  </Flex>
                )}
              </Flex>
              <Flex a="center" g={8}>
                {isReportEditMode && (
                  <IconButton
                    outlined={false}
                    icon={<DeleteOutlined />}
                    onClick={onRemoveClick}
                    sizeType="m"
                  />
                )}
              </Flex>
            </DashboardCardHeader>
            <ChartWrapper>
              <ReportChartProvider
                chartAlias={chart.alias}
                chart={chartParams}
                tableQuery={tableQuery}
                onHistoricalChange={updateHistorical}
              />
            </ChartWrapper>
          </Style>
        </Transition>
      </StyledDashboardCard>
    </StyledDashboardCardWrapper>
  );
};

const Style = styled(VFlex)`
  height: 100%;
`;

export default ReportChartItem;
